import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, Fingerprint, Scan, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useCustomer } from '../context/CustomerContext';
import AirlockTransition from '../components/admin/AirlockTransition';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

// Son de bip futuriste
const playBeep = () => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 800;
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

// Son d'erreur
const playError = () => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.value = 200;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch (e) {}
};

// Son de succès
const playSuccess = () => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523, 659, 784].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, ctx.currentTime + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.15);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + i * 0.1 + 0.15);
    });
  } catch (e) {}
};

export default function VendorLoginPage() {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showAirlock, setShowAirlock] = useState(false);
  const [scanLine, setScanLine] = useState(false);
  const navigate = useNavigate();
  const { setIsVendorAuthenticated, setVendorName, setVendorCode, setIsAdmin } = useCustomer();

  // Easter egg : Séquence secrète pour le cockpit (triple tap rapide)
  const [secretTaps, setSecretTaps] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);

  const handleSecretTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 500) {
      const newTaps = secretTaps + 1;
      setSecretTaps(newTaps);
      
      if (newTaps >= 3) {
        // Accès secret déverrouillé !
        playSuccess();
        toast.success('🔓 Accès Commandant activé !', { icon: '🚀' });
        setShowAirlock(true);
        setSecretTaps(0);
      }
    } else {
      setSecretTaps(1);
    }
    setLastTapTime(now);
  };

  const handleAirlockComplete = () => {
    setShowAirlock(false);
    navigate('/team-lpe/admin');
  };

  const handleKeyPress = (digit) => {
    if (pin.length < 6) {
      playBeep();
      const newPin = pin + digit;
      setPin(newPin);
      
      if (newPin.length === 6) {
        handleSubmit(newPin);
      }
    }
  };

  const handleDelete = () => {
    if (pin.length > 0) {
      playBeep();
      setPin(pin.slice(0, -1));
    }
  };

  const handleClear = () => {
    setPin('');
  };

  const handleSubmit = async (code) => {
    setIsLoading(true);
    setScanLine(true);
    
    try {
      const result = await api.validateVendorCode(code);
      if (result.valid) {
        playSuccess();
        setIsVendorAuthenticated(true);
        setVendorName(result.name);
        setVendorCode(code);
        setIsAdmin(result.isAdmin || false);
        toast.success(`Bienvenue ${result.name} !`, { icon: '✨' });
        
        setTimeout(() => {
          navigate('/team-lpe/search');
        }, 500);
      }
    } catch (error) {
      playError();
      toast.error('Code non reconnu');
      setPin('');
      setScanLine(false);
    } finally {
      setIsLoading(false);
      setTimeout(() => setScanLine(false), 1000);
    }
  };

  // Clavier hexagonal futuriste
  const keypadLayout = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['C', '0', '⌫'],
  ];

  return (
    <>
      {/* Animation du sas pour accès cockpit */}
      <AirlockTransition 
        isActive={showAirlock} 
        onComplete={handleAirlockComplete}
        userName="Commandant"
      />
      
      <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-x-hidden overflow-y-auto bg-[#030308]">
        
        {/* Fond spatial animé */}
        <div className="absolute inset-0">
          {/* Étoiles */}
          {[...Array(80)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: Math.random() * 2 + 1,
                height: Math.random() * 2 + 1,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
          
          {/* Nébuleuse cyan */}
          <div 
            className="absolute w-[600px] h-[600px] rounded-full opacity-20 blur-[150px]"
            style={{
              background: 'radial-gradient(circle, #00d4ff 0%, transparent 70%)',
              left: '-10%',
              top: '20%',
            }}
          />
          
          {/* Nébuleuse violette */}
          <div 
            className="absolute w-[500px] h-[500px] rounded-full opacity-15 blur-[120px]"
            style={{
              background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)',
              right: '-5%',
              bottom: '10%',
            }}
          />
          
          {/* Grille de fond */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(to right, #00d4ff 1px, transparent 1px),
                linear-gradient(to bottom, #00d4ff 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        {/* Bouton retour */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute top-6 left-6 z-20"
        >
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 border border-cyan-500/30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Accueil
          </Button>
        </motion.div>

        {/* Container principal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-sm"
        >
          
          {/* Logo avec Easter egg */}
          <motion.div 
            className="text-center mb-6 cursor-pointer select-none"
            onClick={handleSecretTap}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              animate={{ 
                filter: [
                  'drop-shadow(0 0 20px rgba(0, 212, 255, 0.5))',
                  'drop-shadow(0 0 40px rgba(0, 212, 255, 0.8))',
                  'drop-shadow(0 0 20px rgba(0, 212, 255, 0.5))',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img 
                src={LOGO_URL} 
                alt="La Petite Épicerie" 
                className="h-20 mx-auto"
                draggable={false}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </motion.div>
            <motion.h1 
              className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              ESPACE ÉQUIPE
            </motion.h1>
          </motion.div>

          {/* Terminal d'authentification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            {/* Cadre holographique */}
            <div 
              className="absolute -inset-[2px] rounded-3xl opacity-50"
              style={{
                background: 'linear-gradient(135deg, #00d4ff, #8b5cf6, #00d4ff)',
                filter: 'blur(4px)',
              }}
            />
            
            {/* Contenu principal */}
            <div 
              className="relative rounded-3xl p-6 border border-cyan-500/30"
              style={{
                background: 'linear-gradient(180deg, rgba(0,20,40,0.95) 0%, rgba(0,10,20,0.98) 100%)',
                backdropFilter: 'blur(20px)',
              }}
            >
              {/* Header du terminal */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  <span className="text-cyan-400 text-sm font-mono">AUTHENTIFICATION</span>
                </div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <div className="w-2 h-2 rounded-full bg-cyan-400" />
                  <div className="w-2 h-2 rounded-full bg-purple-400" />
                </div>
              </div>

              {/* Ligne de scan */}
              <AnimatePresence>
                {scanLine && (
                  <motion.div
                    initial={{ top: '0%' }}
                    animate={{ top: '100%' }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: 'linear' }}
                    className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent z-20"
                    style={{ boxShadow: '0 0 20px #00d4ff' }}
                  />
                )}
              </AnimatePresence>

              {/* Affichage du code */}
              <div className="mb-6">
                <div className="flex items-center justify-center gap-1 mb-2">
                  <Fingerprint className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500 text-xs font-mono">CODE SÉCURISÉ</span>
                </div>
                
                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <motion.div
                      key={i}
                      className={`
                        w-11 h-14 rounded-xl flex items-center justify-center text-2xl font-bold font-mono
                        border-2 transition-all duration-200
                        ${pin[i] 
                          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400 shadow-[0_0_15px_rgba(0,212,255,0.3)]' 
                          : 'border-gray-700 bg-gray-900/50 text-gray-700'
                        }
                      `}
                      animate={pin[i] ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.15 }}
                    >
                      {pin[i] ? (showPin ? pin[i] : '◆') : '○'}
                    </motion.div>
                  ))}
                </div>
                
                {/* Toggle visibilité */}
                <button
                  onClick={() => setShowPin(!showPin)}
                  className="mt-3 mx-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-400 transition-colors"
                >
                  {showPin ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showPin ? 'Masquer' : 'Afficher'}
                </button>
              </div>

              {/* Clavier numérique futuriste */}
              <div className="space-y-2">
                {keypadLayout.map((row, rowIdx) => (
                  <div key={rowIdx} className="flex justify-center gap-2">
                    {row.map((key) => {
                      const isSpecial = key === 'C' || key === '⌫';
                      const isZero = key === '0';
                      
                      return (
                        <motion.button
                          key={key}
                          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0,212,255,0.4)' }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            if (key === 'C') handleClear();
                            else if (key === '⌫') handleDelete();
                            else handleKeyPress(key);
                          }}
                          disabled={isLoading}
                          className={`
                            w-16 h-14 rounded-xl font-bold text-xl font-mono
                            border transition-all duration-200
                            disabled:opacity-50 disabled:cursor-not-allowed
                            ${isSpecial 
                              ? key === 'C'
                                ? 'border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20'
                                : 'border-yellow-500/50 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
                              : 'border-cyan-500/30 bg-cyan-500/5 text-white hover:bg-cyan-500/15 hover:border-cyan-400'
                            }
                          `}
                        >
                          {key}
                        </motion.button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Indicateur de chargement */}
              <AnimatePresence>
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 flex items-center justify-center gap-2"
                  >
                    <Scan className="w-4 h-4 text-cyan-400 animate-pulse" />
                    <span className="text-cyan-400 text-sm font-mono">VÉRIFICATION...</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer du terminal */}
              <div className="mt-6 pt-4 border-t border-gray-800">
                <div className="flex items-center justify-center gap-2 text-gray-600 text-xs">
                  <Zap className="w-3 h-3" />
                  <span className="font-mono">SYSTÈME SÉCURISÉ v3.0</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Message subliminal (très discret) */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            transition={{ delay: 5 }}
            className="text-center text-[9px] text-gray-600 mt-4 font-mono select-none"
          >
            {secretTaps > 0 ? `◈`.repeat(secretTaps) : ''}
          </motion.p>
        </motion.div>
      </div>
    </>
  );
}
